<template>
  <v-container class="d-flex justify-center px-xs-0">
    <v-card
      :id="step.id"
      :loading="loading"
      class="overview-card elevation-10"
      :class="{
        'future-step': !step.show && !step.completed,
        'current-step': isCurrentStep,
        complete: step.completed && step.style === 'default',
        'complete-secondary': step.completed && step.style === 'secondary',
        'update-step': step.revise,
      }"
    >
      <div class="header-container">
        <span
          v-if="step.forceStepDescription !== undefined"
          class="step-number"
          >{{ step.forceStepDescription }}</span
        >
        <span v-else class="step-number"
          >STEP {{ step.stepNumber }}
          <span v-if="!step.completed"> - ({{ step.estimatedTime }})</span>
        </span>
        <span v-if="step.revise" class="revision-notice">
          <img
            :src="syncCircleSvg"
            alt="Sync Circle Icon"
            class="revision-sync"
          />
          Update Step
        </span>
        <span
          v-else-if="step.completed && step.hideCompletedBadge !== true"
          class="completed-notice"
        >
          <v-icon color="white">{{ icons.mdiCheckBold }}</v-icon>
          Complete
        </span>
      </div>
      <div class="step-info-container">
        <span class="step-title">{{ step.title }}</span>
        <p class="step-description">{{ step.text }}</p>
      </div>
      <v-card-actions
        v-if="isCurrentStep || step.completed"
        class="card-actions"
        :class="{ bordered: isCurrentStep || step.completed }"
      >
        <GradientButton
          :title="
            step.forceButtonText !== undefined
              ? step.forceButtonText
              : `Step ${step.stepNumber}`
          "
          :small="true"
          :type="'button'"
          :callout="'right'"
          :omk-callout="true"
          :disabled="!isCurrentStep && !step.completed"
          :applied-style="step.style"
          @gradientButtonClicked="
            navigateTo(
              isCurrentStep ? step.redirectLocation : step.editLocation
            )
          "
        />
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
  import { mdiCheckBold } from '@mdi/js';
  import syncCircleSvg from '@/assets/shared/sync_circle.svg';

  export default {
    name: 'OverviewCard',
    props: {
      step: {
        type: Object,
        required: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      icons: {
        mdiCheckBold: mdiCheckBold,
        syncCircle: syncCircleSvg,
      },
      syncCircleSvg: syncCircleSvg,
    }),
    methods: {
      navigateTo(path) {
        this.$router.push({ path: path });
      },
    },
    computed: {
      isCurrentStep() {
        return !this.loading && this.step.show && !this.step.completed;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .overview-card {
    max-width: 500px;
    width: 500px;
    padding: 20px 40px 14px 40px;
    border: none;
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
    ::v-deep .v-progress-linear {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      margin: 0;
      z-index: 99;
      height: 6px !important;
      transition: all 0.333s linear;
    }

    &.complete {
      border-top: 6px solid var(--v-primary-base);
      transition: border 0.1s linear;
    }

    &.complete-secondary {
      border-top: 6px solid var(--v-tertiary-base);
      transition: border 0.1s linear;
    }

    &.update-step {
      border-top: 6px solid #ff9800;
      transition: border 0.1s linear;
    }
    .header-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      .step-number {
        color: #626262;
        font-size: 14px;
        font-weight: 600;
        line-height: 21px;
      }

      .completed-notice {
        color: white;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 14px;
        width: 105px;
        height: 24px;
        border-radius: 100px;
        border: 1px;
        background-color: var(--v-primary-base);
        font-size: 11px;
        font-weight: 700;
        line-height: 16.5px;
        ::v-deep .v-icon {
          height: 10px;
          width: 13px;
        }
      }
    }

    .step-info-container {
      display: flex;
      flex-direction: column;
      color: #282828;
      padding-top: 20px;
      height: 101px;
      min-height: 101px;
      padding-bottom: 16px;
      box-sizing: content-box;
      .step-title {
        font-size: 20px;
        font-weight: 600;
        line-height: 30px;
        margin-bottom: 8px;
      }
      .step-description {
        font-size: 16px;
        font-weight: 400;
        line-height: 20.8px;
      }
    }

    .card-text {
      text-align: left;
      padding-top: 0;
      padding-bottom: 0;
    }

    .card-actions {
      padding: 0;
      padding-top: 16px;
      .step-edit {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0;
        width: 94px;
        height: 36px;
        border-radius: 100px;
        border: 1px solid #dcddde;
        color: #282828;
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
        letter-spacing: 0.4px;
        text-align: center;
        cursor: pointer;
      }
    }

    .card-actions.bordered {
      border-top: 1px solid #dcddde;
    }
  }

  .future-step {
    opacity: 0.3;
  }

  .overview-card-image {
    margin: 0px 5px 15px 5px;
  }

  .revision-notice {
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3px 10px 3px 10px;
    width: 121px;
    height: 24px;
    border-radius: 100px;
    border: 1px;
    background-color: #ff9800;
    font-size: 11px;
    font-weight: 700;
    line-height: 16.5px;
    .revision-sync {
      margin-right: 6px;
      height: 18px;
      width: 18px;
    }
  }

  @media only screen and (min-width: 0px) and (max-width: 480px) {
    .overview-card {
      .step-title {
        font-size: 16px !important;
      }
      .step-description {
        font-size: 14px !important;
      }
      .step-info-container {
        max-height: unset;
        height: auto;
      }
    }
  }
</style>
